import { useSearchParams } from "react-router-dom";
import usePostMount from "hooks/core/usePostMount";
import {
  registerReferenceCar,
  registerReferenceTripFrom,
  registerReferenceExperienceId,
  registerReferenceTripTravelMode,
  setReferenceTripDestinations,
  setReferenceTripLegModes,
} from "redux/slices/shed/shedSlice";
import { TripShare } from "types/user";
import { CarGenericState } from "types/cars";
import { TravelMode, EXPERIENCE_ID } from "types/map";
import { useAppDispatch } from "redux/hooks";
import useGenericCars from "hooks/cars/useGenericCars";
import useGlobalCars from "hooks/cars/useGlobalCars";
import { genericCarToCar } from "libraries/car";
import { buildDestinationsFromTripShare, buildLegModesFromTripShare } from "libraries/trip";
import { getPlaceTypesForIconCode } from "libraries/placeTypeIcons";
import { find } from "lodash/fp";

const STORAGE_KEY_EXPERIENCE_ID = "shed-experience-id";

type Options = {
  defaultExperienceId?: EXPERIENCE_ID;
  useFranceRentalFleet?: boolean;
  /** When false, ignore URL/localStorage experience (main `/h/map`). Default true. */
  applyExperience?: boolean;
};

const useMapShareTripParams = (options: Options = {}) => {
  const {
    defaultExperienceId,
    useFranceRentalFleet = false,
    applyExperience = true,
  } = options;
  const [searchParams] = useSearchParams();
  const { getLocalCars } = useGenericCars();
  const { getGlobalCars } = useGlobalCars();
  const dispatch = useAppDispatch();

  usePostMount({
    handler: () => {
      const tripShare: TripShare = {
        travelMode: (searchParams.get("travelMode") ?? "") as TravelMode,
        genericVin: searchParams.get("genericVin") ?? "",
        fromLabel: searchParams.get("fromLabel") ?? "",
        fromCoordinates: searchParams.get("fromCoordinates") ?? ";",
        toLabel: searchParams.get("toLabel") ?? "",
        toCoordinates: searchParams.get("toCoordinates") ?? ";",
        waypointLabels: searchParams.get("waypointLabels") ?? undefined,
        waypointCoordinates: searchParams.get("waypointCoordinates") ?? undefined,
        pt: searchParams.get("pt") ?? undefined,
        legs: searchParams.get("legs") ?? undefined,
        experienceId: (searchParams.get("experienceId") ?? "") as EXPERIENCE_ID,
      };

      console.log("[Map] Shared trip consummed", tripShare);

      const fromLatitude = Number(tripShare.fromCoordinates.split(";")[0] || 0);
      const fromLongitude = Number(
        tripShare.fromCoordinates.split(";")[1] || 0
      );

      const hasTripShareParams =
        searchParams.has("fromLabel") ||
        searchParams.has("toLabel") ||
        searchParams.has("fromCoordinates") ||
        searchParams.has("toCoordinates");

      if (hasTripShareParams) {
        dispatch(
          registerReferenceTripFrom({
            formattedAddress: tripShare.fromLabel ?? "",
            latitude: fromLatitude,
            longitude: fromLongitude,
            placeTypes: getPlaceTypesForIconCode(tripShare.pt?.[0]),
          })
        );

        const destinations = buildDestinationsFromTripShare(tripShare);
        dispatch(setReferenceTripDestinations(destinations));
        dispatch(
          setReferenceTripLegModes(
            buildLegModesFromTripShare(tripShare, destinations.length)
          )
        );
      }

      const isFranceRentalVin = tripShare.genericVin.startsWith("MLF-");
      const resolveFromFranceFleet = useFranceRentalFleet || isFranceRentalVin;

      const carFleet = resolveFromFranceFleet
        ? getGlobalCars()
        : getLocalCars();

      const genericCar = find(
        ({ genericVin }: CarGenericState) => genericVin === tripShare.genericVin
      )(carFleet);

      genericCar && dispatch(registerReferenceCar(genericCarToCar(genericCar)));

      tripShare.travelMode &&
        dispatch(registerReferenceTripTravelMode(tripShare.travelMode));

      if (!applyExperience) {
        return;
      }

      if (tripShare.experienceId) {
        localStorage.setItem(STORAGE_KEY_EXPERIENCE_ID, tripShare.experienceId);
      }

      const localExperienceId = localStorage.getItem(STORAGE_KEY_EXPERIENCE_ID);
      const currentExperienceId =
        tripShare.experienceId || defaultExperienceId || localExperienceId;
      currentExperienceId &&
        dispatch(registerReferenceExperienceId(currentExperienceId));
    },
  });
};

export default useMapShareTripParams;
